/**
 * @file ConseilsCoachsReussirWod.tsx
 * @summary Article de blog — ConseilsCoachsReussirWod
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/conseils-coachs-reussir-wod-crossfit";
const TITLE = "Comment réussir ses WOD : les conseils des coachs";
const DESC = "RPE, pacing, respiration, technique, intensité : les conseils des coachs CrossFit Chistera pour réussir ses WOD sans s'épuiser ni se blesser.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Comment savoir si je vais trop vite sur un WOD ?", a: "Si vous devez vous arrêter avant la moitié du chrono alors que ce n'était pas prévu, vous êtes parti trop vite. Cible : finir plus fort que vous n'avez commencé." },
  { q: "Faut-il toujours pousser à 100% ?", a: "Non. Sur 5 séances par semaine, peut-être 1 à 2 vraiment proches de la limite. Le reste à RPE 7-8. C'est ce qui permet de progresser sans casser." },
  { q: "Je termine toujours bon dernier, est-ce un problème ?", a: "Non. La performance absolue ne dit rien — l'effort relatif et la régularité, oui. Beaucoup de bons CrossFitteurs étaient derniers au début." },
  { q: "Comment respirer pendant un thruster ou un wall ball ?", a: "Inspire en descente, expire en remontée. Ne jamais bloquer la respiration sur des séries longues — sauf très ponctuellement sur une charge max." },
];

export default function ConseilsCoachsReussirWod() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Comment réussir ses WOD : les conseils des coachs"
      breadcrumbLabel="Réussir ses WOD"
      category="Performance · WOD"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-22" readingMinutes={10}
      ctaVariant="default"
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-22", author, reviewer, faq })}
    >
      <EnBref>
        <p>Bien réussir un WOD, ce n'est pas tout donner et finir au sol. C'est <strong>doser, respirer, garder une technique propre</strong> et terminer plus fort qu'on n'a commencé. Cinq leviers font toute la différence : RPE, pacing, respiration, technique, intensité. On les passe en revue.</p>
      </EnBref>

      <h2>1. Comprendre le RPE (effort perçu)</h2>
      <p>Le RPE (Rate of Perceived Exertion) est une échelle de 1 à 10 :</p>
      <ul>
        <li><strong>RPE 6-7</strong> : on parle, c'est inconfortable mais soutenable longtemps.</li>
        <li><strong>RPE 8</strong> : on parle par mots courts, on transpire fort.</li>
        <li><strong>RPE 9</strong> : on ne parle plus, c'est le seuil mental.</li>
        <li><strong>RPE 10</strong> : tout donné, on ne peut pas tenir 30 secondes de plus.</li>
      </ul>
      <p>La plupart des WOD se courent à RPE 8. Pas 10. C'est la première erreur du débutant.</p>

      <h2>2. Le pacing : démarrer plus lent qu'on ne croit</h2>
      <p>Sur un AMRAP de 12 minutes, démarrer comme sur un sprint vous explose à 4 minutes. Stratégie générale : <strong>premier tiers contrôlé, deuxième tiers à allure cible, dernier tiers libéré</strong>. Sur un "for time" long, c'est encore plus vrai.</p>

      <h2>3. La respiration : votre meilleur outil</h2>
      <p>La règle de base : <strong>inspirer en phase facile, expirer en phase d'effort</strong>. Sur un thruster, inspirer en descente, expirer en remontée. Sur un kettlebell swing, idem. Sur le rameur, inspirer en retour de chariot, expirer en tirée.</p>
      <p>Sur des séries longues, ne jamais bloquer la respiration. Le blocage Valsalva est réservé à la charge max ponctuelle (deadlift max, squat max).</p>

      <h2>4. La technique : le seul sujet qui n'est pas négociable</h2>
      <p>Quand la fatigue arrive, la technique se dégrade. C'est normal — mais il y a des limites. <strong>Si votre dos s'arrondit sur un deadlift, si vos genoux rentrent sur un squat, si votre coude tombe sur un overhead : on s'arrête, on souffle, on repart propre</strong>. Toujours.</p>
      <p>C'est le coach qui veille à ça. Et c'est non négociable, même en compétition. Voir aussi notre article sur les <Link to="/blog/competitions-crossfit-open-lyon">compétitions CrossFit autour de Lyon</Link>.</p>

      <h2>5. L'intensité : à doser sur la semaine</h2>
      <p>Sur 4 à 5 WOD par semaine, viser :</p>
      <ul>
        <li><strong>1 à 2 séances "fort"</strong> (RPE 8-9, où vous êtes vraiment cuit à la fin)</li>
        <li><strong>2 à 3 séances "intermédiaires"</strong> (RPE 7-8, technique propre)</li>
        <li><strong>1 séance "récupération active"</strong> (RPE 5-6, mobilité, technique)</li>
      </ul>
      <p>C'est ce dosage qui fait progresser sans s'épuiser.</p>

      <h2>L'erreur la plus fréquente</h2>
      <p>Ne pas scaler. Vouloir faire RX coûte que coûte alors que ce n'est pas le moment. Conséquence : technique cassée, séance ratée, parfois blessure. Scaler, c'est pro — pas l'inverse. Voir notre article sur la <Link to="/blog/competiteurs-crossfit-chistera-debutants">cohabitation compétiteurs/débutants</Link>.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Comment se passe une séance de CrossFit", to: "/blog/comment-se-passe-une-seance-crossfit" },
        { label: "Calculateur de charges 1RM", to: "/blog/calculateur-charges-crossfit-1rm" },
        { label: "Coaching individuel selon vos objectifs", to: "/blog/coaching-individuel-objectifs-sportifs" },
      ]} />

      <SourcesSection items={[
        { label: "ACSM — Borg Rating of Perceived Exertion", href: "https://www.acsm.org/" },
        { label: "CrossFit Journal — Pacing strategies", href: "https://journal.crossfit.com/" },
      ]} />
    </ArticleLayout>
  );
}
